import { useState } from 'react';
import { useDebounce, useUnmount } from 'react-use';
import { Button } from '@ds';
import { BoardLayoutModeSwitcher } from '@lp/features/CasesBoard/components/BoardLayoutModeSwitcher';
import { useCurrentUser } from '@lp/core/hooks/useCurrentUser';
import { SearchInputWithHistory, FilterDropdown } from '@ui';
import { ResourceFilter } from '@lp/features/CasesBoard/components/ResourceFilter/ResourceFilter';
import { MdOutlineClear } from 'react-icons/md';
import { CaseTypeFilterDropdown } from '@features/components/CaseFilters/CaseTypeFilterDropdown';
import { OPOFilterDropdown } from '@features/components/CaseFilters/OPOFilterDropdown';
import {
  DEFAULT_BOARD_FILTERS,
  useCasesBoardStore,
  useCasesFiltersStore,
  useCasesSearchStore,
} from '@/store/cases-board.store';
import styles from './CasesBoardFilterPanel.module.scss';
import { BoardSettings } from '../BoardSettings/BoardSettings';
import {
  caseQuoteStatusItems,
  donorTypesItems,
  groundConfirmationItems,
  logisticsStatusItems,
  organsItems,
  providersItems,
  randomizationFilterTypeItems,
} from './constants';
import { TUserPreferences, UserPreferences } from '@/api/apis/user';
import { useTrialProgramsList } from '@/hooks/useTrialProgramsList';
import { checkHasFilters } from '@/utils/helpers';
import { serviceRegionItems, QUICK_FILTERS, quickFilterItems } from '@/constants/filters';
import { getQuickFiltersItems } from '@/utils/helpers/quickFiltersItems.helpers';
import { TXCentersFilterDropdown } from '../TXCentersFilterDropdown/TXCentersFilterDropdown';

export const CasesBoardFilterPanel = () => {
  const { updateUserPreferences } = useCurrentUser();
  const isRows = useCasesBoardStore((state) => state.isRows);
  const setBoardPreferences = useCasesBoardStore((store) => store.setBoardPreferences);
  const { searchQuery, setSearchQuery } = useCasesSearchStore();
  const { setFilters, ...filters } = useCasesFiltersStore();
  const [searchText, setSearchText] = useState(searchQuery);
  const { trialProgramsList } = useTrialProgramsList();

  useDebounce(
    () => {
      setSearchQuery(searchText);
    },
    500,
    [searchText],
  );

  const toggleLayout = () => {
    setBoardPreferences({ isRows: !isRows });
    updateUserPreferences([{ name: UserPreferences.isRows, value: JSON.stringify(!isRows) }]);
  };

  const saveFilters = (filtersData: { name: string; value: unknown }[]) => {
    const nextFilters = Object.fromEntries(filtersData.map(({ name, value }) => [name, value]));
    setFilters(nextFilters);

    updateUserPreferences(
      filtersData.map((f) => ({
        name: f.name as TUserPreferences,
        value: JSON.stringify(f.value),
      })),
    );
  };

  useUnmount(() => {
    setSearchQuery('');
  });

  const selectUsers = (value: string[]) => {
    saveFilters([
      { name: UserPreferences.assignedMe, value: false },
      { name: UserPreferences.assignedUsers, value },
    ]);
  };

  const selectQuickFilters = (values: string[]) => {
    const recent = values.includes(QUICK_FILTERS.recent);
    const assignedMe = values.includes(QUICK_FILTERS.assignedMe);
    const favorites = values.includes(QUICK_FILTERS.favorites);
    const duplicates = values.includes(QUICK_FILTERS.duplicates);

    saveFilters([
      { name: UserPreferences.recent, value: recent },
      { name: UserPreferences.assignedMe, value: assignedMe },
      { name: UserPreferences.favorites, value: favorites },
      { name: UserPreferences.duplicates, value: duplicates },
      ...(assignedMe ? [{ name: UserPreferences.assignedUsers, value: [] }] : []),
    ]);
  };

  const hasFilters = checkHasFilters(filters, DEFAULT_BOARD_FILTERS);

  const clearFilters = () => {
    const filtersMapped = Object.entries(DEFAULT_BOARD_FILTERS).map(([name, value]) => ({ name, value }));
    saveFilters(filtersMapped);
    setSearchQuery('');
    setSearchText('');
  };

  return (
    <div className={styles.container}>
      <div className="flex flex-wrap gap-4 items-center">
        <SearchInputWithHistory handleSearch={setSearchText} searchText={searchText} storageKey="boardCasesHistory" />
        <ResourceFilter title="Resources" selectedKeys={filters.assignedUsers} onSelectedKeysChange={selectUsers} />
        <FilterDropdown
          title="Organ"
          items={organsItems}
          selectedKeys={filters.organ}
          mode="multiple"
          onSelectedKeysChange={(value) => saveFilters([{ name: UserPreferences.organ, value }])}
          size="sm"
          showResetButton
        />
        <FilterDropdown
          title="Donor Type"
          items={donorTypesItems}
          selectedKeys={filters.donorType}
          mode="single"
          onSelectedKeysChange={(value) => saveFilters([{ name: UserPreferences.donorType, value }])}
          size="sm"
        />
        <CaseTypeFilterDropdown
          size="sm"
          opoRequest={filters.opoRequest}
          isReallocation={filters.isReallocatedFromSource}
          onFiltersChange={(nextOpoRequest, nextIsReallocation) => {
            saveFilters([
              { name: UserPreferences.opoRequest, value: nextOpoRequest },
              { name: UserPreferences.isReallocatedFromSource, value: nextIsReallocation },
            ]);
          }}
        />
        <FilterDropdown
          title="Quick Filters"
          items={quickFilterItems}
          selectedKeys={getQuickFiltersItems(filters)}
          mode="multiple"
          onSelectedKeysChange={selectQuickFilters}
          size="sm"
          showResetButton
        />
        <TXCentersFilterDropdown
          selectedKeys={filters.txCenterIds}
          onSelectedKeysChange={(value) => saveFilters([{ name: UserPreferences.txCenterIds, value }])}
          showResetButton
        />
        <OPOFilterDropdown
          selectedKeys={filters.opoIds}
          onSelectedKeysChange={(value) => saveFilters([{ name: UserPreferences.opoIds, value }])}
          showResetButton
        />
        <FilterDropdown
          title="Logistics Provider"
          items={providersItems}
          selectedKeys={filters.logisticProviderTypes}
          mode="multiple"
          onSelectedKeysChange={(value) => saveFilters([{ name: UserPreferences.logisticProviderTypes, value }])}
          size="sm"
          showResetButton
        />
        <FilterDropdown
          title="Logistics Status"
          items={logisticsStatusItems}
          selectedKeys={filters.routePlanningStatuses}
          mode="multiple"
          onSelectedKeysChange={(value) => saveFilters([{ name: UserPreferences.routePlanningStatuses, value }])}
          size="sm"
          showResetButton
        />
        <FilterDropdown
          title="Quote Status"
          items={caseQuoteStatusItems}
          selectedKeys={filters.quoteStatuses}
          mode="multiple"
          onSelectedKeysChange={(value) => saveFilters([{ name: UserPreferences.quoteStatuses, value }])}
          size="sm"
          showResetButton
        />
        <FilterDropdown
          title="Ground Confirmation"
          items={groundConfirmationItems}
          selectedKeys={filters.groundConfirmationStatuses}
          mode="single"
          onSelectedKeysChange={(value) => saveFilters([{ name: UserPreferences.groundConfirmationStatuses, value }])}
          size="sm"
        />
        <FilterDropdown
          title="Trial Programs"
          items={trialProgramsList}
          selectedKeys={filters.trialProgramIds}
          mode="multiple"
          onSelectedKeysChange={(value) => saveFilters([{ name: UserPreferences.trialProgramIds, value }])}
          size="sm"
          showResetButton
        />
        <FilterDropdown
          title="Randomization Outcome"
          items={randomizationFilterTypeItems}
          selectedKeys={filters.randomizationResultTypes}
          mode="multiple"
          onSelectedKeysChange={(value) => saveFilters([{ name: UserPreferences.randomizationResultTypes, value }])}
          size="sm"
          showResetButton
        />
        <FilterDropdown
          title="Service Region"
          items={serviceRegionItems}
          selectedKeys={filters.serviceRegionIds}
          mode="multiple"
          onSelectedKeysChange={(value) => saveFilters([{ name: UserPreferences.serviceRegionIds, value }])}
          size="sm"
          showResetButton
        />
        {hasFilters && (
          <Button startContent={<MdOutlineClear size={18} />} size="sm" variant="light" onClick={clearFilters}>
            Clear all
          </Button>
        )}
      </div>
      <div className={styles.boardViewBlock}>
        <BoardSettings />
        <BoardLayoutModeSwitcher checked={!isRows} onChange={toggleLayout} />
      </div>
    </div>
  );
};
